import {
  WSElement,
  WSElementProps,
  WSText
} from "@wingspanhq/fe-component-library";

import { Deduction, DeductionStatus } from "@wingspanhq/wingspan-api-sdk";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import { Currency } from "@wingspanhq/utils/dist/currency/currencies";
import classNames from "classnames";
import React from "react";
import { useHistory } from "react-router-dom";
import { queriesPayables, queriesPayees } from "@query";
import { WSQueries } from "../../../query/WSQuery";
import styles from "./DeductionsHistoryList.module.scss";
import { selectorCounterpartyDisplayName } from "../../../shared/selectors/selectorCounterpartyDisplayName";

type Props = WSElementProps & {
  deduction: Deduction;
};

// v3 payable read (was v1 `paymentsService.payable.get`).
const usePayableQuery = (payableId: string) =>
  queriesPayables.getPayable.useQuery({ payableId });

export const PayableLink: React.FC<{ payableId: string }> = ({ payableId }) => {
  const history = useHistory();
  const qPayable = usePayableQuery(payableId);

  return (
    <WSQueries
      queries={{
        qPayable
      }}
      renderErrors={() => null}
      renderLoader={() => (
        <WSText.ParagraphSm inline shimmer>
          #11111
        </WSText.ParagraphSm>
      )}
    >
      {({ qPayable }) => (
        <WSText.ParagraphSm
          color="blue500"
          inline
          onClick={() =>
            history.push(`/member/invoices/payables/all/${qPayable.data.id}`)
          }
        >
          #{qPayable.data.invoiceNumber}
        </WSText.ParagraphSm>
      )}
    </WSQueries>
  );
};
/**
 * The contractor the deduction belongs to. v1 read the member profile by
 * `memberId`; v3 `Deduction` carries `payeeId`, so this reads the payee and
 * names it with the shared counterparty selector.
 */
export const PayeeName: React.FC<{ payeeId: string }> = ({ payeeId }) => {
  const qPayee = queriesPayees.getPayee.useQuery({ payeeId });

  return (
    <WSQueries
      queries={{
        qPayee
      }}
      renderErrors={() => null}
      renderLoader={() => (
        <WSText.ParagraphSm inline shimmer>
          Contractor
        </WSText.ParagraphSm>
      )}
    >
      {({ qPayee }) => (
        <WSText.ParagraphSm inline color="gray600" as="b">
          {selectorCounterpartyDisplayName(qPayee.data)}
        </WSText.ParagraphSm>
      )}
    </WSQueries>
  );
};

export const PayableHistoryItem: React.FC<{
  payableId: string;
  amountDeducted: number;
}> = ({ payableId, amountDeducted }) => {
  const history = useHistory();
  const qPayable = usePayableQuery(payableId);

  return (
    <WSQueries
      queries={{
        qPayable
      }}
      renderErrors={() => "-"}
      renderLoader={() => (
        <WSElement shimmer>
          <WSText weight="medium" mb="XS" formatDate="monthDayYear">
            Loading
          </WSText>
          <WSText.ParagraphSm color="gray500">-</WSText.ParagraphSm>
        </WSElement>
      )}
    >
      {({ qPayable }) => (
        <>
          <WSText weight="medium" mb="XS" formatDate="monthDayYear">
            {qPayable.data.events?.paidAt}
          </WSText>
          <WSText.ParagraphSm color="gray500">
            {`${formatMoney(
              amountDeducted,
              qPayable.data.currency as Currency
            )} deducted from payable `}
            <WSText.ParagraphSm
              color="blue500"
              inline
              onClick={() =>
                history.push(
                  `/member/invoices/payables/all/${qPayable.data.id}`
                )
              }
            >
              #{qPayable.data.invoiceNumber}
            </WSText.ParagraphSm>
          </WSText.ParagraphSm>
        </>
      )}
    </WSQueries>
  );
};

export const DeductionsHistoryList: React.FC<Props> = ({
  deduction,
  ...elementProps
}) => {
  const history = useHistory();

  return (
    <WSElement mb="2XL" {...elementProps}>
      <WSText weight="medium" mt="2XL" mb="M">
        History
      </WSText>
      <>
        {(deduction.application || []).map(
          (application, index, applications) => {
            // v1 asked "do the DISBURSED applications add up to the full
            // amount?" via `application[].disbursementId`, which v3's
            // `DeductionApplicationInner` (amountDeducted / payableId /
            // appliedAt) does not carry. The deduction's own status is the
            // direct answer to the same question.
            const isSuccess =
              index === 0 && deduction.status === DeductionStatus.COMPLETE;

            return (
              <WSElement
                key={`${index}_key`}
                className={classNames(styles.item, {
                  [styles.success]: isSuccess,
                  [styles.filled]: !isSuccess
                })}
                mb="XL"
              >
                <WSElement className={styles.icon} />
                <PayableHistoryItem
                  payableId={application.payableId as string}
                  amountDeducted={application.amountDeducted ?? 0}
                />
              </WSElement>
            );
          }
        )}

        {/*
          The "Scheduled start date" entry is gone: v1 `IDeduction.startDate`
          has no v3 counterpart — neither `Deduction` nor `DeductionCreate`
          carries a start date, so there is nothing to render.
          See V3_MIGRATION_TRACKER.md · /v3/payments/deductions.
        */}

        <WSElement className={classNames(styles.item)} mb="XL">
          <WSText weight="medium" mb="XS" formatDate="monthDayYear">
            {deduction.events?.createdAt}
          </WSText>
          <WSText.ParagraphSm color="gray500">
            {formatMoney(
              deduction.amount ?? 0,
              (deduction.currency ?? Currency.USD) as Currency
            ) + " "}
            deduction created by <PayeeName payeeId={deduction.payeeId ?? ""} />
          </WSText.ParagraphSm>
        </WSElement>
      </>
    </WSElement>
  );
};
